import { signalStoreFeature } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { authEvents } from './events';
import { TermsAndConditionsChecksum } from '../../models/auth';
import { AUTH_STORAGE_KEYS } from '../../constants/auth-storage';

export type State = {
  loading: boolean;
  isAuthenticated: boolean;
  email: string;
  email_verified: string;
  family_name: string;
  given_name: string;
  phone_number: string;
  phone_number_verified: string;
  'custom:hq_country': string;
  'custom:operating_countries': string;
  'custom:user_type': string;
  'custom:parent_sub': string;
  'custom:terms_hash'?: string;
  address: string;
  sub: string;
  resendConfirmation: boolean;
  confirmationEmail: string;
  changePassEmail: string; // maybe reuse confirmationEmail for both
  termsAndConditionsChecksum: TermsAndConditionsChecksum | null;
  termsAndConditionsPresignedUrl: string | null;
  termsAccepted: boolean;
};

/** Reads tokens from storage on each call — do not cache at module load for resets. */
export function readIsAuthenticatedFromStorage(): boolean {
  return (
    !!localStorage.getItem(AUTH_STORAGE_KEYS.idToken) ||
    !!localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken)
  );
}

/** Fresh auth slice for bootstrap / session restore. */
export function createAuthState(): State {
  const hasSession = readIsAuthenticatedFromStorage();
  return {
    /** When tokens exist, stay "loading" until `getUser` finishes so guards see `custom:terms_hash`. */
    loading: hasSession,
    sub: '',
    email: '',
    email_verified: '',
    family_name: '',
    given_name: '',
    phone_number: '',
    phone_number_verified: '',
    address: '',
    'custom:hq_country': '',
    'custom:operating_countries': '',
    'custom:user_type': '',
    'custom:parent_sub': '',
    'custom:terms_hash': undefined,
    isAuthenticated: hasSession,
    resendConfirmation: false,
    confirmationEmail: '',
    changePassEmail: '',
    termsAndConditionsChecksum: null,
    termsAndConditionsPresignedUrl: null,
    termsAccepted: false,
  };
}

/** Used after logout so a stale module-level `isAuthenticated` cannot survive. */
export function createLoggedOutAuthState(): State {
  return {
    ...createAuthState(),
    isAuthenticated: false,
    loading: false,
  };
}

export const initialState: State = createAuthState();

export function withAuthReducer() {
  return signalStoreFeature(
    withReducer(
      on(authEvents.clear, () => createLoggedOutAuthState()),
      on(authEvents.setLoading, ({ payload: loading }) => ({ loading })),
      on(authEvents.loginSuccess, ({ payload: isAuthenticated }) => ({
        isAuthenticated,
      })),
      on(authEvents.refreshSuccess, ({ payload: isAuthenticated }) => ({
        isAuthenticated,
      })),
      on(authEvents.getUserSuccess, ({ payload: user }) => ({ ...user })),
      on(
        authEvents.setResendConfirmation,
        ({ payload: resendConfirmation }) => ({ resendConfirmation }),
      ),
      on(authEvents.setConfirmationEmail, ({ payload: confirmationEmail }) => ({
        confirmationEmail,
      })),
      on(authEvents.setChangePassEmail, ({ payload: changePassEmail }) => ({
        changePassEmail,
      })),
      on(authEvents.logoutSuccess, () => createLoggedOutAuthState()),
      on(
        authEvents.loadedTermsAndConditionsChecksum,
        ({ payload: termsAndConditionsChecksum }) => ({
          termsAndConditionsChecksum,
        }),
      ),
      on(
        authEvents.loadedTermsAndConditionsPresignedUrl,
        ({ payload: termsAndConditionsPresignedUrl }) => ({
          termsAndConditionsPresignedUrl,
        }),
      ),
      on(authEvents.termsAccepted, ({ payload: termsAccepted }) => ({
        termsAccepted,
      })),
    ),
  );
}
